import React, { type FC, type PropsWithChildren, useCallback, useMemo, useState } from "react";
import { Alert, Button, Checkbox, cn, FieldError, InputGroup, Label, Spinner, TextField, toast } from "@heroui/react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { Envelope, Eye, EyeSlash } from "@gravity-ui/icons";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { captureException } from "@sentry/react";
import { GQL } from "@binale-tech/shared";
import { useMutation } from "@apollo/client/react";

import { AppRoutes } from "@domain/routing/routeConstants";
import { AuthLayout } from "./AuthLayout";
import { LogoHeader } from "./LogoHeader";
import { auth } from "@domain/api/firebase/firebase";
import { getFirebaseErrorMessageByError } from "@domain/api/firebase/firebaseErrorMessages";
import { legalAcceptanceRecord } from "@domain/graphql/legalAcceptance";
import { logger } from "@domain/infrastructure/logger";

interface RegisterForm {
    email: string;
    password: string;
    password_repeat: string;
    /** Contract acceptance - the AGB are incorporated by ticking this (§ 305 Abs. 2 BGB). */
    agb: boolean;
    /** Art. 13 DSGVO acknowledgement of the privacy notice and the cookie information. */
    privacy: boolean;
}

/**
 * The two account-level documents recorded at registration. Both are account level,
 * so `companyId` stays omitted - the AVV is the only per-company acceptance.
 */
const ACCOUNT_LEVEL_DOCUMENTS = [GQL.ILegalDocumentType.Agb, GQL.ILegalDocumentType.Privacy] as const;

// Legal texts open in a new tab: the links sit inside the checkbox labels, so an
// in-place navigation would discard the half-filled registration form.
const AGB_VALUES = {
    agbLink: (
        <Link to={AppRoutes.agb} target="_blank">
            <FormattedMessage id="app.page.legal.agb" />
        </Link>
    ),
};

const PRIVACY_VALUES = {
    datenschutzLink: (
        <Link to={AppRoutes.datenschutz} target="_blank">
            <FormattedMessage id="app.page.legal.datenschutz" />
        </Link>
    ),
    cookieLink: (
        <Link to={AppRoutes.cookies} target="_blank">
            <FormattedMessage id="app.page.legal.cookiePolicy" />
        </Link>
    ),
};

/**
 * Wraps a consent checkbox with its validation message. HeroUI's `FieldError` belongs
 * to a React Aria field context that `Checkbox` does not provide, so the message is
 * rendered here and announced via `role="alert"`.
 */
const ConsentField: FC<PropsWithChildren<{ error?: string }>> = ({ error, children }) => (
    <div className="flex flex-col gap-1">
        {children}
        {error ? (
            <span className="text-danger text-sm" role="alert">
                {error}
            </span>
        ) : null}
    </div>
);

export const RegisterView = () => {
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);

    const { control, handleSubmit, watch } = useForm<RegisterForm>();

    const [recordAcceptance] = useMutation(legalAcceptanceRecord);

    /**
     * Records the AGB and privacy acceptances. Runs *after* the Firebase account
     * exists, so it must never reject: a failed recording may not block the user and
     * there is nothing to roll back. It must not be silent either - an acceptance that
     * was ticked but never stored is a compliance gap, so every failure goes to Sentry
     * (plus the console) where it stays visible.
     */
    const recordLegalAcceptances = useCallback(async () => {
        const results = await Promise.allSettled(
            ACCOUNT_LEVEL_DOCUMENTS.map((documentType) => recordAcceptance({ variables: { input: { documentType } } })),
        );
        results.forEach((result, index) => {
            const documentType = ACCOUNT_LEVEL_DOCUMENTS[index];
            if (result.status === "rejected") {
                logger.error("legalAcceptanceRecord failed", documentType, result.reason);
                captureException(result.reason, {
                    tags: { legalAcceptance: documentType },
                    extra: { documentType, stage: "registration" },
                });
                return;
            }
            // The mutation is `Boolean!`: anything but `true` means the row was not
            // written, which is just as much of a gap as a rejected request.
            if (result.value.data?.legalAcceptanceRecord !== true) {
                const notRecorded = new Error("legalAcceptanceRecord did not record the acceptance");
                logger.error(notRecorded.message, documentType);
                captureException(notRecorded, {
                    tags: { legalAcceptance: documentType },
                    extra: { documentType, stage: "registration" },
                });
            }
        });
    }, [recordAcceptance]);

    const onSubmit = async (values: RegisterForm) => {
        setLoading(true);
        try {
            await createUserWithEmailAndPassword(auth, values.email, values.password);
            // From here on the account exists - nothing below may abort the registration.
            await recordLegalAcceptances();
            await sendEmailVerification(auth.currentUser);
            toast(formatMessage({ id: "app.registration.message.send_verification_email" }));
            navigate(AppRoutes.home);
        } catch (err) {
            logger.error({ err });
            setError(getFirebaseErrorMessageByError(err));
            setLoading(false);
        }
    };

    const toggleShowPassword = useCallback(() => setShowPassword((v) => !v), []);
    const toggleShowPasswordRepeat = useCallback(() => setShowPasswordRepeat((v) => !v), []);

    const emailRules = useMemo(
        () => ({
            required: formatMessage({ id: "app.validation.error.field_empty" }),
            pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: formatMessage({ id: "app.validation.error.invalid_email" }),
            },
        }),
        [formatMessage],
    );
    const passwordRules = useMemo(
        () => ({
            required: formatMessage({ id: "app.validation.error.field_empty" }),
            minLength: {
                value: 6,
                message: formatMessage({ id: "app.validation.error.short_password" }, { length: 6 }),
            },
        }),
        [formatMessage],
    );
    const passwordRepeatRules = useMemo(
        () => ({
            required: formatMessage({ id: "app.validation.error.field_empty" }),
            validate: (value: string) =>
                value === watch("password") || formatMessage({ id: "app.validation.error.passwords_not_match" }),
        }),
        [formatMessage, watch],
    );
    // Both consents are mandatory and validated the same way, but they are two separate
    // legal acts - contract acceptance (AGB) and Art. 13 information (privacy/cookies) -
    // so they are two separate fields with two separate acceptance records.
    const consentRules = useMemo(
        () => ({
            validate: (value: boolean) => value || formatMessage({ id: "app.validation.error.consent_required" }),
        }),
        [formatMessage],
    );

    return (
        <AuthLayout>
            <LogoHeader />
            {error && (
                <Alert status="danger" className="mb-4">
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            <FormattedMessage id={error} />
                        </Alert.Title>
                    </Alert.Content>
                </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <Controller
                    name="email"
                    control={control}
                    rules={emailRules}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks the controlled-field typing without a tsc gate here
                    render={({ field, fieldState }) => (
                        <TextField
                            aria-label="E-Mail"
                            isInvalid={Boolean(fieldState.error)}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        >
                            <InputGroup>
                                <InputGroup.Input
                                    type="email"
                                    placeholder={formatMessage({ id: "app.global.email" })}
                                    autoComplete="username"
                                    ref={field.ref}
                                />
                                <InputGroup.Suffix>
                                    <Envelope className="size-4" />
                                </InputGroup.Suffix>
                            </InputGroup>
                            <FieldError>{fieldState.error?.message}</FieldError>
                        </TextField>
                    )}
                />
                <Controller
                    name="password"
                    control={control}
                    rules={passwordRules}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks the controlled-field typing without a tsc gate here
                    render={({ field, fieldState }) => (
                        <TextField
                            aria-label="Passwort"
                            isInvalid={Boolean(fieldState.error)}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        >
                            <InputGroup>
                                <InputGroup.Input
                                    type={showPassword ? "text" : "password"}
                                    placeholder={formatMessage({ id: "app.global.password" })}
                                    autoComplete="new-password"
                                    ref={field.ref}
                                />
                                <InputGroup.Suffix>
                                    <button type="button" onClick={toggleShowPassword}>
                                        {showPassword ? <EyeSlash className="size-4" /> : <Eye className="size-4" />}
                                    </button>
                                </InputGroup.Suffix>
                            </InputGroup>
                            <FieldError>{fieldState.error?.message}</FieldError>
                        </TextField>
                    )}
                />
                <Controller
                    name="password_repeat"
                    control={control}
                    rules={passwordRepeatRules}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks the controlled-field typing without a tsc gate here
                    render={({ field, fieldState }) => (
                        <TextField
                            aria-label="Passwort wiederholen"
                            isInvalid={Boolean(fieldState.error)}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        >
                            <InputGroup>
                                <InputGroup.Input
                                    type={showPasswordRepeat ? "text" : "password"}
                                    placeholder={formatMessage({ id: "app.global.password_repeat" })}
                                    autoComplete="new-password"
                                    ref={field.ref}
                                />
                                <InputGroup.Suffix>
                                    <button type="button" onClick={toggleShowPasswordRepeat}>
                                        {showPasswordRepeat ? (
                                            <EyeSlash className="size-4" />
                                        ) : (
                                            <Eye className="size-4" />
                                        )}
                                    </button>
                                </InputGroup.Suffix>
                            </InputGroup>
                            <FieldError>{fieldState.error?.message}</FieldError>
                        </TextField>
                    )}
                />
                <Controller
                    name="agb"
                    control={control}
                    rules={consentRules}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks the controlled-field typing without a tsc gate here
                    render={({ field, fieldState }) => (
                        <ConsentField error={fieldState.error?.message}>
                            <Checkbox
                                isSelected={Boolean(field.value)}
                                onChange={field.onChange}
                                isInvalid={Boolean(fieldState.error)}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control className={cn({ "border border-border-tertiary": !field.value })}>
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                    <Label>
                                        <FormattedMessage id="app.registration.agb.agree" values={AGB_VALUES} />
                                    </Label>
                                </Checkbox.Content>
                            </Checkbox>
                        </ConsentField>
                    )}
                />
                <Controller
                    name="privacy"
                    control={control}
                    rules={consentRules}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks the controlled-field typing without a tsc gate here
                    render={({ field, fieldState }) => (
                        <ConsentField error={fieldState.error?.message}>
                            <Checkbox
                                isSelected={Boolean(field.value)}
                                onChange={field.onChange}
                                isInvalid={Boolean(fieldState.error)}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control className={cn({ "border border-border-tertiary": !field.value })}>
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                    <Label>
                                        <FormattedMessage id="app.registration.privacy.agree" values={PRIVACY_VALUES} />
                                    </Label>
                                </Checkbox.Content>
                            </Checkbox>
                        </ConsentField>
                    )}
                />
                <Button type="submit" isDisabled={loading} isPending={loading} fullWidth size="lg">
                    {({ isPending }) => (
                        <>
                            {isPending ? <Spinner color="current" size="sm" /> : null}
                            <FormattedMessage id="app.button.register" tagName="span" />
                        </>
                    )}
                </Button>
            </form>
            <div>
                <FormattedMessage id="app.global.already_have_account" />
                &nbsp;
                <Link to={AppRoutes.authLogin}>
                    <FormattedMessage id="app.global.login_now" />
                </Link>
            </div>
        </AuthLayout>
    );
};
